import { AuthRouting } from '@/auth/auth-routing';
import { RequireAuth } from '@/auth/require-auth';
import { ErrorRouting } from '@/errors/error-routing';
import { AdminLayout } from '@/layouts/admin-layout';
import {
  AccountActivityPage,
  AccountAllowedIPAddressesPage,
  AccountApiKeysPage,
  AccountAppearancePage,
  AccountBackupAndRecoveryPage,
  AccountBasicPage,
  AccountCompanyProfilePage,
  AccountCurrentSessionsPage,
  AccountDeviceManagementPage,
  AccountEnterprisePage,
  AccountGetStartedPage,
  AccountHistoryPage,
  AccountImportMembersPage,
  AccountIntegrationsPage,
  AccountInviteAFriendPage,
  AccountMembersStarterPage,
  AccountNotificationsPage,
  AccountOverviewPage,
  AccountPermissionsCheckPage,
  AccountPermissionsTogglePage,
  AccountPlansPage,
  AccountPrivacySettingsPage,
  AccountRolesPage,
  AccountSecurityGetStartedPage,
  AccountSecurityLogPage,
  AccountSettingsEnterprisePage,
  AccountSettingsModalPage,
  AccountSettingsPlainPage,
  AccountSettingsSidebarPage,
  AccountTeamInfoPage,
  AccountTeamMembersPage,
  AccountTeamsPage,
  AccountTeamsStarterPage,
  AccountUserProfilePage,
} from '@/pages/account';
import { AdminDashboard } from '@/pages/admin/dashboard/admin-dashboard';
import { UserPermissionsDebug } from '@/pages/admin/debug/UserPermissionsDebug';
import FeedbackAdminPage from '@/pages/admin/feedback';
import { PermissionsListPage } from '@/pages/admin/permissions';
import { RoleDetailsPage, RolesListPage } from '@/pages/admin/roles';
import {
  UserActivityTimelinePage,
  UserBulkOperationsPage,
  UserDataExportPage,
  UserDetailsPage,
  UserLookupPage,
  UserRolesPermissionsPage,
  UsersListPage,
  UserWalletManagementPage,
} from '@/pages/admin/users';
import {
  AuthAccountDeactivatedPage,
  AuthWelcomeMessagePage,
} from '@/pages/auth';
import { DefaultPage, Demo1DarkSidebarPage } from '@/pages/dashboards';
import { CheckInAnalyticsPage } from '@/pages/gamification/checkin/analytics-page';
import { CheckInConfigPage } from '@/pages/gamification/checkin/config-page';
import { ReferralManagementPage } from '@/pages/gamification/referrals';
import { RewardsListPage } from '@/pages/gamification/rewards';
import {
  WalletDetailsPage,
  WalletsListPage,
} from '@/pages/gamification/wallets';
import { WalletAnalyticsPage } from '@/pages/gamification/wallets/analytics-page';
import { WalletAuditLogPage } from '@/pages/gamification/wallets/audit-log-page';
import { WalletStatisticsPage } from '@/pages/gamification/wallets/statistics-page';
import {
  NetworkAppRosterPage,
  NetworkAuthorPage,
  NetworkGetStartedPage,
  NetworkMarketAuthorsPage,
  NetworkMiniCardsPage,
  NetworkNFTPage,
  NetworkSaasUsersPage,
  NetworkSocialPage,
  NetworkStoreClientsPage,
  NetworkUserCardsTeamCrewPage,
  NetworkUserTableTeamCrewPage,
  NetworkVisitorsPage,
} from '@/pages/network';
import {
  CampaignsCardPage,
  CampaignsListPage,
  ProfileActivityPage,
  ProfileBloggerPage,
  ProfileCompanyPage,
  ProfileCreatorPage,
  ProfileCRMPage,
  ProfileDefaultPage,
  ProfileEmptyPage,
  ProfileFeedsPage,
  ProfileGamerPage,
  ProfileModalPage,
  ProfileNetworkPage,
  ProfileNFTPage,
  ProfilePlainPage,
  ProfileTeamsPage,
  ProfileWorksPage,
  ProjectColumn2Page,
  ProjectColumn3Page,
} from '@/pages/public-profile';
import { AllProductsPage, DashboardPage } from '@/pages/store-admin';
import {
  MyOrdersPage,
  OrderPlacedPage,
  OrderReceiptPage,
  OrderSummaryPage,
  PaymentMethodPage,
  ProductDetailsPage,
  SearchResultsGridPage,
  SearchResultsListPage,
  ShippingInfoPage,
  StoreClientPage,
  WishlistPage,
} from '@/pages/store-client';
import { Navigate, Route, Routes } from 'react-router';

export function AppRoutingSetup() {
  return (
    <Routes>
      <Route element={<RequireAuth />}>
        <Route element={<AdminLayout />}>
          {/* Admin Dashboard */}
          <Route path="/" element={<AdminDashboard />} />

          {/* Debug Route - Check User Permissions */}
          <Route path="/debug/permissions" element={<UserPermissionsDebug />} />

          {/* Roles & Permissions Management - Phase 1 Week 4 */}
          <Route path="/auth/roles" element={<RolesListPage />} />
          <Route path="/auth/roles/:id" element={<RoleDetailsPage />} />
          <Route path="/auth/permissions" element={<PermissionsListPage />} />

          {/* User Management - Phase 1 Week 5 */}
          <Route path="/users" element={<UsersListPage />} />
          <Route path="/users/lookup" element={<UserLookupPage />} />
          <Route path="/users/export" element={<UserDataExportPage />} />
          <Route path="/users/roles" element={<UserRolesPermissionsPage />} />
          <Route path="/users/activity" element={<UserActivityTimelinePage />} />
          <Route path="/users/wallet" element={<UserWalletManagementPage />} />
          <Route path="/users/bulk" element={<UserBulkOperationsPage />} />
          <Route path="/users/:id" element={<UserDetailsPage />} />

          {/* Gamification Routes - Phase 2 Week 5 */}
          <Route path="/gamification/wallets" element={<WalletsListPage />} />
          <Route
            path="/gamification/wallets/analytics"
            element={<WalletAnalyticsPage />}
          />
          <Route
            path="/gamification/wallets/audit-log"
            element={<WalletAuditLogPage />}
          />
          <Route
            path="/gamification/wallets/statistics"
            element={<WalletStatisticsPage />}
          />
          <Route
            path="/gamification/wallets/:userId"
            element={<WalletDetailsPage />}
          />
          <Route
            path="/gamification/feedback"
            element={<FeedbackAdminPage />}
          />
          <Route
            path="/gamification/referrals"
            element={<ReferralManagementPage />}
          />
          <Route
            path="/gamification/checkin/config"
            element={<CheckInConfigPage />}
          />
          <Route
            path="/gamification/checkin/analytics"
            element={<CheckInAnalyticsPage />}
          />
          <Route path="/gamification/rewards" element={<RewardsListPage />} />

          {/* Feedback management */}
          <Route path="/admin/feedback" element={<FeedbackAdminPage />} />

          {/* Placeholder routes - will be implemented progressively */}
          <Route
            path="/gamification/*"
            element={
              <div className="p-6">
                <h1 className="text-2xl font-bold">
                  Gamification - Coming Soon
                </h1>
              </div>
            }
          />
          <Route
            path="/content/*"
            element={
              <div className="p-6">
                <h1 className="text-2xl font-bold">
                  Content Management - Coming Soon
                </h1>
              </div>
            }
          />
          <Route
            path="/notifications/*"
            element={
              <div className="p-6">
                <h1 className="text-2xl font-bold">
                  Notifications - Coming Soon
                </h1>
              </div>
            }
          />
          <Route
            path="/analytics/*"
            element={
              <div className="p-6">
                <h1 className="text-2xl font-bold">Analytics - Coming Soon</h1>
              </div>
            }
          />
          <Route
            path="/monitor/*"
            element={
              <div className="p-6">
                <h1 className="text-2xl font-bold">
                  Activity Monitor - Coming Soon
                </h1>
              </div>
            }
          />
          <Route
            path="/settings/*"
            element={
              <div className="p-6">
                <h1 className="text-2xl font-bold">Settings - Coming Soon</h1>
              </div>
            }
          />
          <Route
            path="/security/*"
            element={
              <div className="p-6">
                <h1 className="text-2xl font-bold">Security - Coming Soon</h1>
              </div>
            }
          />

          {/* Legacy demo routes - keep for reference, will be removed later */}
          <Route path="/demo" element={<DefaultPage />} />
          <Route path="/dark-sidebar" element={<Demo1DarkSidebarPage />} />
          <Route
            path="/public-profile/profiles/default/"
            element={<ProfileDefaultPage />}
          />
          <Route
            path="/public-profile/profiles/creator"
            element={<ProfileCreatorPage />}
          />
          <Route
            path="/public-profile/profiles/company"
            element={<ProfileCompanyPage />}
          />
          <Route
            path="/public-profile/profiles/nft"
            element={<ProfileNFTPage />}
          />
          <Route
            path="/public-profile/profiles/blogger"
            element={<ProfileBloggerPage />}
          />
          <Route
            path="/public-profile/profiles/crm"
            element={<ProfileCRMPage />}
          />
          <Route
            path="/public-profile/profiles/gamer"
            element={<ProfileGamerPage />}
          />
          <Route
            path="/public-profile/profiles/feeds"
            element={<ProfileFeedsPage />}
          />
          <Route
            path="/public-profile/profiles/plain"
            element={<ProfilePlainPage />}
          />
          <Route
            path="/public-profile/profiles/modal"
            element={<ProfileModalPage />}
          />
          <Route
            path="/public-profile/projects/3-columns"
            element={<ProjectColumn3Page />}
          />
          <Route
            path="/public-profile/projects/2-columns"
            element={<ProjectColumn2Page />}
          />
          <Route path="/public-profile/works" element={<ProfileWorksPage />} />
          <Route path="/public-profile/teams" element={<ProfileTeamsPage />} />
          <Route
            path="/public-profile/network"
            element={<ProfileNetworkPage />}
          />
          <Route
            path="/public-profile/activity"
            element={<ProfileActivityPage />}
          />
          <Route
            path="/public-profile/campaigns/card"
            element={<CampaignsCardPage />}
          />
          <Route
            path="/public-profile/campaigns/list"
            element={<CampaignsListPage />}
          />
          <Route path="/public-profile/empty" element={<ProfileEmptyPage />} />
          <Route
            path="/account/home/get-started"
            element={<AccountGetStartedPage />}
          />
          <Route
            path="/account/home/user-profile"
            element={<AccountUserProfilePage />}
          />
          <Route
            path="/account/home/company-profile"
            element={<AccountCompanyProfilePage />}
          />
          <Route
            path="/account/home/settings-sidebar"
            element={<AccountSettingsSidebarPage />}
          />
          <Route
            path="/account/home/settings-enterprise"
            element={<AccountSettingsEnterprisePage />}
          />
          <Route
            path="/account/home/settings-plain"
            element={<AccountSettingsPlainPage />}
          />
          <Route
            path="/account/home/settings-modal"
            element={<AccountSettingsModalPage />}
          />
          <Route path="/account/billing/basic" element={<AccountBasicPage />} />
          <Route
            path="/account/billing/enterprise"
            element={<AccountEnterprisePage />}
          />
          <Route path="/account/billing/plans" element={<AccountPlansPage />} />
          <Route
            path="/account/billing/history"
            element={<AccountHistoryPage />}
          />
          <Route
            path="/account/security/get-started"
            element={<AccountSecurityGetStartedPage />}
          />
          <Route
            path="/account/security/overview"
            element={<AccountOverviewPage />}
          />
          <Route
            path="/account/security/allowed-ip-addresses"
            element={<AccountAllowedIPAddressesPage />}
          />
          <Route
            path="/account/security/privacy-settings"
            element={<AccountPrivacySettingsPage />}
          />
          <Route
            path="/account/security/device-management"
            element={<AccountDeviceManagementPage />}
          />
          <Route
            path="/account/security/backup-and-recovery"
            element={<AccountBackupAndRecoveryPage />}
          />
          <Route
            path="/account/security/current-sessions"
            element={<AccountCurrentSessionsPage />}
          />
          <Route
            path="/account/security/security-log"
            element={<AccountSecurityLogPage />}
          />
          <Route
            path="/account/members/team-starter"
            element={<AccountTeamsStarterPage />}
          />
          <Route path="/account/members/teams" element={<AccountTeamsPage />} />
          <Route
            path="/account/members/team-info"
            element={<AccountTeamInfoPage />}
          />
          <Route
            path="/account/members/members-starter"
            element={<AccountMembersStarterPage />}
          />
          <Route
            path="/account/members/team-members"
            element={<AccountTeamMembersPage />}
          />
          <Route
            path="/account/members/import-members"
            element={<AccountImportMembersPage />}
          />
          <Route path="/account/members/roles" element={<AccountRolesPage />} />
          <Route
            path="/account/members/permissions-toggle"
            element={<AccountPermissionsTogglePage />}
          />
          <Route
            path="/account/members/permissions-check"
            element={<AccountPermissionsCheckPage />}
          />
          <Route
            path="/account/integrations"
            element={<AccountIntegrationsPage />}
          />
          <Route
            path="/account/notifications"
            element={<AccountNotificationsPage />}
          />
          <Route path="/account/api-keys" element={<AccountApiKeysPage />} />
          <Route
            path="/account/appearance"
            element={<AccountAppearancePage />}
          />
          <Route
            path="/account/invite-a-friend"
            element={<AccountInviteAFriendPage />}
          />
          <Route path="/account/activity" element={<AccountActivityPage />} />
          <Route
            path="/network/get-started"
            element={<NetworkGetStartedPage />}
          />
          <Route
            path="/network/user-cards/mini-cards"
            element={<NetworkMiniCardsPage />}
          />
          <Route
            path="/network/user-cards/team-crew"
            element={<NetworkUserCardsTeamCrewPage />}
          />
          <Route
            path="/network/user-cards/author"
            element={<NetworkAuthorPage />}
          />
          <Route path="/network/user-cards/nft" element={<NetworkNFTPage />} />
          <Route
            path="/network/user-cards/social"
            element={<NetworkSocialPage />}
          />
          <Route
            path="/network/user-table/team-crew"
            element={<NetworkUserTableTeamCrewPage />}
          />
          <Route
            path="/network/user-table/app-roster"
            element={<NetworkAppRosterPage />}
          />
          <Route
            path="/network/user-table/market-authors"
            element={<NetworkMarketAuthorsPage />}
          />
          <Route
            path="/network/user-table/saas-users"
            element={<NetworkSaasUsersPage />}
          />
          <Route
            path="/network/user-table/store-clients"
            element={<NetworkStoreClientsPage />}
          />
          <Route
            path="/network/user-table/visitors"
            element={<NetworkVisitorsPage />}
          />
          <Route
            path="/auth/welcome-message"
            element={<AuthWelcomeMessagePage />}
          />
          <Route
            path="/auth/account-deactivated"
            element={<AuthAccountDeactivatedPage />}
          />
          <Route path="/store-client/home" element={<StoreClientPage />} />
          <Route
            path="/store-client/search-results-grid"
            element={<SearchResultsGridPage />}
          />
          <Route
            path="/store-client/search-results-list"
            element={<SearchResultsListPage />}
          />
          <Route
            path="/store-client/product-details"
            element={<ProductDetailsPage />}
          />
          <Route path="/store-client/wishlist" element={<WishlistPage />} />
          <Route
            path="/store-client/checkout/order-summary"
            element={<OrderSummaryPage />}
          />
          <Route
            path="/store-client/checkout/shipping-info"
            element={<ShippingInfoPage />}
          />
          <Route
            path="/store-client/checkout/payment-method"
            element={<PaymentMethodPage />}
          />
          <Route
            path="/store-client/checkout/order-placed"
            element={<OrderPlacedPage />}
          />
          <Route path="/store-client/my-orders" element={<MyOrdersPage />} />
          <Route
            path="/store-client/order-receipt"
            element={<OrderReceiptPage />}
          />
          <Route path="/store-admin/dashboard" element={<DashboardPage />} />
          <Route
            path="/store-admin/inventory/all-products"
            element={<AllProductsPage />}
          />
          <Route path="/auth/get-started" element={<AccountGetStartedPage />} />
        </Route>
      </Route>
      <Route path="error/*" element={<ErrorRouting />} />
      <Route path="auth/*" element={<AuthRouting />} />
      <Route path="*" element={<Navigate to="/error/404" />} />
    </Routes>
  );
}
